/**
 * One Escape closes one overlay, the topmost, whichever family it belongs to (F-0533, KD-8;
 * `docs/concept-web-keyboard-accessibility.md`).
 *
 * Two families answer Escape, each with its own stack: a pop-out (`useDismissable`, one capture
 * listener on `document`) and a dialog (`useDialogFocusTrap`, a listener on the dialog's own
 * element). Each was right about its own stack and blind to the other's, so one key closed a pop-out
 * and the dialog it sat in, or a palette and the pop-out open behind it. Measured on `285bfc377`.
 *
 * This is the order between them: every overlay takes a number when it opens, higher is newer. A
 * pop-out leaves the key to a dialog opened after it; a pop-out that spends the key marks it, and a
 * dialog leaves a marked key alone.
 */

let opened = 0;

/** The next overlay's place in the order: higher opened later. */
export function nextOverlayOrder(): number {
  opened += 1;
  return opened;
}

const openDialogs: number[] = [];

/** Puts a dialog in the order while it is open. Returns the removal. */
export function registerDialogOrder(order: number): () => void {
  openDialogs.push(order);
  return () => {
    const at = openDialogs.indexOf(order);
    if (at !== -1) openDialogs.splice(at, 1);
  };
}

/** The newest open dialog's place, or 0 with none open. */
export function newestDialogOrder(): number {
  return openDialogs.length === 0 ? 0 : Math.max(...openDialogs);
}

const spent = new WeakSet<Event>();

/** Marks an Escape as answered: an overlay closed on it, and none beneath may also close. */
export function spendEscape(event: Event): void {
  spent.add(event);
}

/** Whether an overlay above already closed on this Escape. */
export function escapeSpent(event: Event): boolean {
  return spent.has(event);
}
